import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  orchestratorService,
  type ProjectCommandCenterMessage,
} from '@services/orchestratorService';
import {
  ensureProjectBucket,
  isProjectBucketFresh,
  markBucketError,
  markBucketLoading,
  type ProjectBucket,
} from '../utils/projectBucket';
import type { RootState } from '../index';

interface ProjectActivityBucket extends ProjectBucket {
  items: ProjectCommandCenterMessage[];
}

interface ProjectActivityState {
  byProjectId: Record<string, ProjectActivityBucket>;
}

const createBucket = (): ProjectActivityBucket => ({
  items: [],
  isLoading: false,
  error: null,
  lastFetched: null,
});

const initialState: ProjectActivityState = {
  byProjectId: {},
};

type FirestoreTimestampLike = {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
  [key: string]: unknown;
};

const getTimestampPart = (
  record: FirestoreTimestampLike,
  standardKey: 'seconds' | 'nanoseconds',
  privateKey: '_seconds' | '_nanoseconds'
) => {
  if (typeof record[privateKey] === 'number') return record[privateKey] as number;
  if (typeof record[standardKey] === 'number') return record[standardKey] as number;
  return null;
};

const getStringValue = (rawMessage: Record<string, unknown>, keys: string[]) => {
  const matchedKey = keys.find(key => typeof rawMessage[key] === 'string');
  if (!matchedKey) return null;
  return rawMessage[matchedKey] as string;
};

const toIsoString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) return value;
  if (value instanceof Date) return value.toISOString();

  if (value && typeof value === 'object') {
    const record = value as FirestoreTimestampLike;

    if (typeof record.toDate === 'function') {
      const date = record.toDate();
      if (!Number.isNaN(date.getTime())) return date.toISOString();
    }

    const seconds = getTimestampPart(record, 'seconds', '_seconds');
    const nanoseconds = getTimestampPart(record, 'nanoseconds', '_nanoseconds') ?? 0;

    if (seconds !== null) {
      return new Date(seconds * 1000 + nanoseconds / 1_000_000).toISOString();
    }
  }

  return new Date().toISOString();
};

const normalizeMessage = (
  rawMessage: ProjectCommandCenterMessage | Record<string, unknown>
): ProjectCommandCenterMessage => {
  const message = rawMessage as Record<string, unknown>;

  return {
    id: String(message.id ?? ''),
    type: typeof message.type === 'string' ? message.type : 'agent',
    content: typeof message.content === 'string' ? message.content : '',
    pendingActionId: getStringValue(message, ['pendingActionId', 'pending_action_id']),
    activityType: getStringValue(message, ['activityType', 'activity_type']),
    createdAt: toIsoString(message.createdAt ?? message.created_at),
  };
};

export const fetchProjectActivity = createAsyncThunk(
  'projectActivity/fetchProjectActivity',
  async (
    { projectId, force }: { projectId: string; force?: boolean },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const bucket = state.projectActivity.byProjectId[projectId];

    if (isProjectBucketFresh(bucket, force)) {
      return { projectId, items: null as ProjectCommandCenterMessage[] | null };
    }

    try {
      const response = await orchestratorService.getProjectMessages(projectId);
      const items = response
        .map(item => normalizeMessage(item))
        .sort(
          (left, right) => Date.parse(right.createdAt || '') - Date.parse(left.createdAt || '')
        );

      return { projectId, items };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load project activity';
      return rejectWithValue({ projectId, message });
    }
  }
);

const projectActivitySlice = createSlice({
  name: 'projectActivity',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchProjectActivity.pending, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.meta.arg.projectId,
          createBucket
        );
        markBucketLoading(bucket);
      })
      .addCase(fetchProjectActivity.fulfilled, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.payload.projectId,
          createBucket
        );
        bucket.isLoading = false;
        if (action.payload.items !== null) {
          bucket.items = action.payload.items;
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchProjectActivity.rejected, (state, action) => {
        const payload = action.payload as { projectId?: string; message?: string } | undefined;
        const projectId = payload?.projectId ?? action.meta.arg.projectId;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        markBucketError(bucket, payload?.message ?? 'Failed to load project activity');
      });
  },
});

export const selectProjectActivity = (state: RootState, projectId: string) =>
  state.projectActivity.byProjectId[projectId]?.items ?? [];

export const selectProjectActivityLoading = (state: RootState, projectId: string) =>
  state.projectActivity.byProjectId[projectId]?.isLoading ?? false;

export default projectActivitySlice.reducer;
